// Recettes (lot L2-A) : créances (H4-04). Une créance naît d'un fait générateur (contrat commun
// recettes-socle.ts) ou d'un avis d'imposition saisi ; elle porte redevable, rubrique, tarif
// appliqué, période, montant dû, montant payé, échéance et état. Montants entiers en francs CFA.
// Une annulation est un mouvement motivé qui laisse la créance visible en état annulee ; une remise
// (ou une exonération, rubrique liée active) réduit le montant dû avec motif.
import { z } from 'zod';
import { NATURES_FAIT_GENERATEUR, schemaBaseTarification } from './recettes-socle';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const code = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code en minuscules, chiffres et soulignés');

export const ETATS_CREANCE = {
  emise: 'Émise',
  partiellement_payee: 'Partiellement payée',
  soldee: 'Soldée',
  annulee: 'Annulée',
  en_contentieux: 'En contentieux',
} as const;
export type EtatCreance = keyof typeof ETATS_CREANCE;

/** Relances après l'échéance (H4-05) : J+7 et J+30 par défaut, paramètre recettes.relances_jours. */
export const RELANCES_CREANCE_JOURS = [7, 30] as const;
/** Échéance d'une créance de démarche payante, en jours après le dépôt (hypothèse, paramètre recettes). */
export const ECHEANCE_DEMARCHE_JOURS = 30;
/** Seuil de remise au-delà duquel une anomalie est ouverte (pour cent du montant initial, paramètre recettes). */
export const SEUIL_REMISE_POURCENTAGE = 20;

export const schemaFiltreCreances = z.object({
  etat: z.enum(Object.keys(ETATS_CREANCE) as [EtatCreance, ...EtatCreance[]]).optional(),
  redevable_id: uuid.optional(),
  rubrique: code.optional(),
  nature: z.enum(NATURES_FAIT_GENERATEUR).optional(),
  objet_type: code.optional(),
  objet_id: uuid.optional(),
  echue: z.enum(['oui', 'non']).optional(),
  limite: z.coerce.number().int().min(1).max(1000).default(200),
});
export type FiltreCreances = z.infer<typeof schemaFiltreCreances>;

export const schemaExportRecettes = z.object({ format: z.enum(['csv']).default('csv') });

/** Avis d'imposition saisi par un agent : créance sans autre objet métier (H4-04). */
export const schemaSaisieAvis = z.strictObject({
  redevable: z
    .strictObject({
      redevable_id: uuid.optional(),
      usager_compte_id: uuid.optional(),
      nom: z.string().trim().min(1).max(200).optional(),
      type: z.enum(['personne', 'entreprise']).default('personne'),
      contact: z.record(z.string(), z.string().max(200)).optional(),
    })
    .refine((r) => r.redevable_id || r.usager_compte_id || r.nom, { message: 'Désigner le redevable' }),
  rubrique_code: code,
  base: schemaBaseTarification,
  periode: z.object({ debut: jour, fin: jour }).refine((p) => p.fin >= p.debut, { message: 'La fin suit le début', path: ['fin'] }),
  echeance: jour,
  libelle: z.string().trim().min(1).max(300),
});

export const schemaAnnulationCreance = z.strictObject({
  motif: z.string().trim().min(3).max(1000),
  piece_document_id: uuid.optional(),
});

export const schemaRemiseCreance = z.strictObject({
  montant: z.number().int().positive(),
  motif: z.string().trim().min(3).max(1000),
  piece_document_id: uuid.optional(),
  /** Exonération : rubrique liée de catégorie exoneration, active (désactivée par défaut, H4-03). */
  rubrique_exoneration_id: uuid.optional(),
});

export const schemaFiltreRedevables = z.object({ q: z.string().trim().min(1).max(100).optional() });

export const schemaCreanceReponse = z.object({
  id: z.string(),
  numero: z.string(),
  libelle: z.string(),
  redevable: z.object({ id: z.string(), nom: z.string(), type: z.string(), usager_compte_id: z.string().nullable() }),
  rubrique: z.object({ id: z.string(), code: z.string(), libelle: z.string(), paiement_partiel_autorise: z.boolean() }),
  tarif_id: z.string(),
  fait_generateur: z.object({ nature: z.string(), objet_type: z.string(), objet_id: z.string() }),
  periode: z.object({ debut: z.string(), fin: z.string() }),
  montant_initial: z.number(),
  montant_remise: z.number(),
  montant_du: z.number(),
  montant_paye: z.number(),
  reste: z.number(),
  echeance: z.string(),
  echue: z.boolean(),
  etat: z.string(),
  regle: z.string(),
  fictif: z.boolean(),
  mention: z.string().nullable(),
  motif_annulation: z.string().nullable(),
  annulee_le: z.string().nullable(),
  cree_le: z.string(),
});
export type CreanceReponse = z.infer<typeof schemaCreanceReponse>;
